import React from "react";
import { ThreeEvent } from "@react-three/fiber";
import * as THREE from "three";
import {
  SymbolType,
  WallType,
  WindowType,
} from "../../../../../types/floorplannerTypes";
import { resolveMaterial } from "../../../materials/materialResolver";
import { usePointerUpSelection } from "../../../scene/usePointerUpSelection";
import {
  applyWallTextureUVs,
  getWallColor,
} from "../../../structures/walls/wallGeometry";
import {
  DEFAULT_WINDOW_HEIGHT,
  DEFAULT_WINDOW_SILL_HEIGHT,
  Window3DAssemblyGeometry,
  getSymbol3DBaseElevation,
} from "../openingGeometry";
import OpeningWallMass3D from "../OpeningWallMass3D";
import Window3DAssemblyMesh from "./Window3DAssemblyMesh";

const WindowOpening3D: React.FC<{
  wall: WallType;
  symbol: WindowType;
  assembly: Window3DAssemblyGeometry | null;
  selected: boolean;
  shape: THREE.Shape;
  wallHeight: number;
  wallWidth: number;
  wallSelected: boolean;
  renderWallMass?: boolean;
  claimSelectionEvents?: boolean;
  onSelectWall: (wall: WallType, event: ThreeEvent<PointerEvent>) => void;
  onSelectSymbol: (symbol: SymbolType, event: ThreeEvent<PointerEvent>) => void;
  onAngleDragActiveChange: (active: boolean) => void;
}> = ({
  wall,
  symbol,
  assembly,
  selected,
  shape,
  wallHeight,
  wallWidth,
  wallSelected,
  renderWallMass = true,
  claimSelectionEvents = false,
  onSelectWall,
  onSelectSymbol,
  onAngleDragActiveChange,
}) => {
  const wallSelectionHandlers = usePointerUpSelection(wall, onSelectWall, {
    claimPointerEvents: claimSelectionEvents,
  });
  const configureWallTextureUVs = React.useCallback(
    (geometry: THREE.ExtrudeGeometry, positionZ: number) => {
      applyWallTextureUVs(geometry, wall, positionZ);
    },
    [wall],
  );
  const wallMassColor = resolveMaterial("wall", wall, {
    defaultColor: getWallColor(wall),
  }).color;
  const sillHeight = THREE.MathUtils.clamp(
    getSymbol3DBaseElevation(symbol, DEFAULT_WINDOW_SILL_HEIGHT),
    0,
    wallHeight,
  );
  const windowHeight = symbol.threeD?.height ?? DEFAULT_WINDOW_HEIGHT;
  const lintelStart = Math.min(sillHeight + windowHeight, wallHeight);
  const lintelHeight = Math.max(wallHeight - lintelStart, 0);
  const wallMassUserData = {
    floorplannerType: "wall",
    floorplannerId: wall.id,
  };

  return (
    <>
      {renderWallMass && (
        <OpeningWallMass3D
          wall={wall}
          shape={shape}
          depth={Math.min(sillHeight, wallHeight)}
          selected={wallSelected}
          wallWidth={wallWidth}
          fallbackColor={wallMassColor}
          configureGeometry={configureWallTextureUVs}
          userData={wallMassUserData}
          {...wallSelectionHandlers}
        />
      )}
      {renderWallMass && lintelHeight > 0.01 && (
        <OpeningWallMass3D
          wall={wall}
          shape={shape}
          depth={lintelHeight}
          positionZ={lintelStart}
          selected={wallSelected}
          wallWidth={wallWidth}
          fallbackColor={wallMassColor}
          configureGeometry={configureWallTextureUVs}
          userData={wallMassUserData}
          {...wallSelectionHandlers}
        />
      )}
      {assembly && (
        <Window3DAssemblyMesh
          symbol={symbol}
          assembly={assembly}
          selected={selected}
          claimSelectionEvents={claimSelectionEvents}
          onSelect={onSelectSymbol}
          onAngleDragActiveChange={onAngleDragActiveChange}
        />
      )}
    </>
  );
};

export default WindowOpening3D;
